/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file IncomingCallBanner.jsx
 * @description
 * Foreground softphone overlay, mounted once at the navigation root so it floats
 * above every screen:
 *  - an iOS-style, animated slide-down INCOMING-call banner (premium deep-blue
 *    "conversation" card with accept-green / reject-red circles) shown when a
 *    call rings while the app is open — distinct from the native CallKit
 *    lock-screen UI; and
 *  - an ACTIVE-call mini-bar (live timer + mute + hang-up) while a call is up.
 *
 * State + audio come from {@link module:helpers/CallProvider useVoip}; the
 * foreground incoming event is bridged through the in-process Observable
 * (`voip:incoming`) that the provider emits.
 *
 * Pure JavaScript (ESM/JSX), no TypeScript. iOS + Android.
 */

import React, {useEffect, useRef} from 'react';
import {Animated, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import {useVoip} from '../../helpers/CallProvider';
import {BRAND, CONVERSATION} from '../../helpers/managementTheme';
import {useTopInset} from '../../helpers/safeArea';
import Icon from '../management/SafeIcon';
import CallerIdentityBlock from './CallerIdentityBlock';

/**
 * Format a duration in seconds as mm:ss.
 * @param {number} totalSeconds
 * @returns {string}
 */
const formatDuration = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
};

/**
 * The floating incoming-call banner + active-call mini-bar.
 * @returns {JSX.Element|null}
 */
export default function IncomingCallBanner() {
    const {t} = useTranslation();
    const topInset = useTopInset();
    const {status, remoteNumber, identity, identitySearching, muted, durationSeconds, answer, hangup, toggleMute} = useVoip();

    const slide = useRef(new Animated.Value(-160)).current;

    const visible = status === 'ringing-in' || status === 'ringing-out' || status === 'active';

    useEffect(() => {
        Animated.spring(slide, {
            toValue: visible ? 0 : -180,
            useNativeDriver: true,
            friction: 9,
            tension: 70,
        }).start();
    }, [visible, slide]);

    if (!visible) {
        return null;
    }

    const isIncoming = status === 'ringing-in';
    const isActive = status === 'active';

    const subtitle = isIncoming
        ? t('voip_incoming_call', 'Bejövő hívás')
        : isActive
            ? formatDuration(durationSeconds)
            : t('voip_calling', 'Hívás…');

    return (
        <Animated.View
            pointerEvents="box-none"
            style={[styles.wrap, {top: topInset + 8, transform: [{translateY: slide}]}]}
        >
            <LinearGradient
                colors={['#0C4FB0', '#06255E', '#030B24']}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 1}}
                style={styles.card}
            >
                <View style={[styles.avatar, {backgroundColor: isIncoming ? 'rgba(34,197,94,0.22)' : (isActive ? 'rgba(249,115,22,0.24)' : 'rgba(255,255,255,0.14)')}]}>
                    <Icon
                        name={isIncoming ? 'phone-incoming' : isActive ? 'phone-in-talk' : 'phone-outgoing'}
                        size={22}
                        color={isIncoming ? '#4ADE80' : (isActive ? BRAND.light : '#FFFFFF')}
                    />
                </View>

                <View style={styles.info}>
                    <CallerIdentityBlock identity={identity} number={remoteNumber} searching={identitySearching} variant="banner" />
                    <Text style={styles.subtitle} numberOfLines={1}>
                        {subtitle}{isActive && !!remoteNumber ? ` · ${remoteNumber}` : ''}
                    </Text>
                </View>

                <View style={styles.actions}>
                    {isIncoming ? (
                        <>
                            <TouchableOpacity
                                onPress={hangup}
                                activeOpacity={0.85}
                                style={[styles.circle, {backgroundColor: '#EF4444'}]}
                                accessibilityLabel={t('voip_reject', 'Elutasítás')}
                            >
                                <Icon name="phone-hangup" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={answer}
                                activeOpacity={0.85}
                                style={[styles.circle, {backgroundColor: '#22C55E'}]}
                                accessibilityLabel={t('voip_answer', 'Fogadás')}
                            >
                                <Icon name="phone" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                        </>
                    ) : (
                        <>
                            {isActive && (
                                <TouchableOpacity
                                    onPress={toggleMute}
                                    activeOpacity={0.85}
                                    style={[styles.circle, {backgroundColor: muted ? BRAND.base : 'rgba(255,255,255,0.14)', borderColor: muted ? BRAND.base : CONVERSATION.border, borderWidth: 1}]}
                                    accessibilityLabel={t('voip_mute', 'Némítás')}
                                >
                                    <Icon name={muted ? 'microphone-off' : 'microphone'} size={20} color="#FFFFFF" />
                                </TouchableOpacity>
                            )}
                            <TouchableOpacity
                                onPress={hangup}
                                activeOpacity={0.85}
                                style={[styles.circle, {backgroundColor: '#EF4444'}]}
                                accessibilityLabel={t('voip_hangup', 'Hívás vége')}
                            >
                                <Icon name="phone-hangup" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                        </>
                    )}
                </View>
            </LinearGradient>
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    wrap: {
        position: 'absolute',
        left: 12,
        right: 12,
        zIndex: 9999,
        elevation: 24,
    },
    card: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 28,
        borderWidth: 1,
        borderColor: CONVERSATION.border,
        paddingHorizontal: 14,
        paddingVertical: 12,
        overflow: 'hidden',
        shadowColor: '#030B24',
        shadowOffset: {width: 0, height: 12},
        shadowOpacity: 0.4,
        shadowRadius: 20,
        elevation: 12,
    },
    avatar: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    info: {flex: 1, minWidth: 0, paddingRight: 10},
    name: {fontSize: 16, fontWeight: '800', letterSpacing: -0.2, color: CONVERSATION.text},
    subtitle: {fontSize: 13, fontWeight: '600', marginTop: 2, color: CONVERSATION.subText},
    actions: {flexDirection: 'row', alignItems: 'center', gap: 10},
    circle: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
